import { useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { I } from './icons';
import { BrandLogo } from './brand';
import { useApi, useLive, useLiveStatus } from '../hooks';
import { useAuth } from '../auth';
import { DEMO } from '../api';

const NAV: { group: string; items: { to: string; label: string; icon: keyof typeof I; count?: 'approvals' | 'review' }[] }[] = [
  { group: 'Work', items: [{ to: '/inbox', label: 'Inbox', icon: 'Inbox' }, { to: '/approvals', label: 'Approvals', icon: 'Stamp', count: 'approvals' }, { to: '/comments', label: 'Comments', icon: 'Comment', count: 'review' }] },
  { group: 'Teach', items: [{ to: '/knowledge', label: 'Knowledge base', icon: 'Book' }, { to: '/playbooks', label: 'Playbooks', icon: 'Route' }, { to: '/automations', label: 'Comment automations', icon: 'Bolt' }, { to: '/compliance', label: 'Compliance', icon: 'Shield' }] },
  { group: 'Measure', items: [{ to: '/analytics', label: 'Analytics', icon: 'Chart' }, { to: '/quality', label: 'Quality and testing', icon: 'Flask' }] },
  { group: 'Setup', items: [{ to: '/widget', label: 'Website widget', icon: 'Window' }, { to: '/integrations', label: 'Integrations', icon: 'Plug' }, { to: '/settings', label: 'Settings', icon: 'Gear' }] },
];

export function Layout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const live = useLiveStatus();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const approvals = useApi<any[]>('/approvals');
  const review = useApi<any[]>('/comments?status=review');
  const settings = useApi<any>('/settings');
  useLive((e) => { if (e.type.startsWith('approval')) approvals.reload(); if (e.type.startsWith('comment')) review.reload(); });
  const counts = { approvals: approvals.data?.length ?? 0, review: review.data?.length ?? 0 };
  const paused = Boolean(settings.data?.agent?.paused);
  const toggleTheme = () => { const r = document.documentElement; r.dataset.theme = r.dataset.theme === 'dark' ? 'light' : 'dark'; };

  return (
    <div className="shell">
      <header className="topbar">
        <button className="btn sm" onClick={() => setOpen(true)} aria-label="Open navigation"><I.Menu /></button>
        <BrandLogo height={20} /><span style={{ color: 'var(--rail-text)' }}>Assist</span>
      </header>
      <nav className={`rail ${open ? 'open' : ''}`} aria-label="Main" onClick={(e) => { if ((e.target as HTMLElement).closest('a')) setOpen(false); }}>
        <a className="brand" href={DEMO ? '#/inbox' : '/app/inbox'}><BrandLogo height={26} /><span className="brand-sub">Assist: customer conversations</span></a>
        {paused && <div className="badge danger" style={{ margin: '0 8px' }}>Assistant paused</div>}
        {NAV.map((g) => (
          <div className="nav-group" key={g.group}>
            <div className="nav-label">{g.group}</div>
            {g.items.map((it) => {
              const Icon = I[it.icon];
              const n = it.count ? counts[it.count] : 0;
              return <NavLink key={it.to} to={it.to} className={({ isActive }) => `nav-link ${isActive || (it.to === '/inbox' && loc.pathname.startsWith('/inbox')) ? 'active' : ''}`}><Icon />{it.label}{n > 0 && <span className="nav-count" aria-label={`${n} waiting`}>{n}</span>}</NavLink>;
            })}
          </div>
        ))}
        <div className="rail-foot">
          <span className="live"><span className={`live-dot ${live ? '' : 'off'}`} />{live ? 'Live updates on' : 'Reconnecting'}</span>
          <span>{user?.name} <span style={{ opacity: .7 }}>({user?.role})</span></span>
          <button className="rail-btn" onClick={toggleTheme}>Switch light or dark</button>
          {!DEMO && <button className="rail-btn" onClick={logout}>Sign out</button>}
        </div>
      </nav>
      {open && <div className="scrim" onClick={() => setOpen(false)} />}
      <main className="main" id="main">
        {DEMO && <div className="demo-banner">Demo with sample data. Nothing here reaches real customers, and changes reset when you reload.</div>}
        {children}
      </main>
    </div>
  );
}
